import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * The organization's open workspaces as a dialog on the organization pages offers them (Invite someone, Grant
 * role, Joining from a domain) — read from the organization's profile (`getAccountProfile`, which lists every
 * workspace of the organization for any organization role, `account.workspace_metadata.read`), never from the
 * workspaces the person happens to be a member of.
 *
 * Owner, 2026-11-03: "on invite on organization it says no workspace open and i cannot select from dropdown".
 * The dialogs used to read `profile.data?.workspace ?? []`: while the profile was still on its way, or when it
 * failed, that was an empty list, and the workspace choice simply never appeared — with no word as to why. Now
 * the three states are told apart and each is said in words: loading, failed (with Try again), and an
 * organization that has no open workspace at all.
 */
import type { UseQueryResult } from "@tanstack/react-query";
import { Button } from "@wetalk/ui";
import type { ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import type { AccountProfileView, AccountWorkspaceMetadataView } from "../../../api/slice/account-team.js";

export type OrganizationWorkspaceRead =
  | { readonly state: "loading" }
  | { readonly state: "failed"; readonly problem: string; readonly retry: () => void }
  /** `workspace` holds the open (not archived) workspaces only; it may be empty. */
  | { readonly state: "ready"; readonly workspace: readonly AccountWorkspaceMetadataView[] };

/** The organization's open workspaces from its profile read, with the read's own state. */
export function organizationWorkspaceReadOf(profile: UseQueryResult<AccountProfileView>): OrganizationWorkspaceRead {
  if (profile.data !== undefined) return { state: "ready", workspace: profile.data.workspace.filter((one) => !one.archived) };
  if (profile.isError) {
    return {
      state: "failed",
      problem: apiProblemText(profile.error, portalText("core", "account_admin.workspace_choice_failed")),
      retry: () => {
        void profile.refetch();
      },
    };
  }
  return { state: "loading" };
}

/** The open workspaces to choose from — none until the read is ready. */
export function openWorkspaceOf(read: OrganizationWorkspaceRead): readonly AccountWorkspaceMetadataView[] {
  return read.state === "ready" ? read.workspace : [];
}

/**
 * The words in place of the workspace choice when there is nothing to choose from yet: loading, failed (with
 * Try again), or no open workspace in the organization. `null` once there are workspaces to choose from.
 */
export function WorkspaceChoiceStatus(props: { readonly read: OrganizationWorkspaceRead }): ReactNode {
  usePortalTranslation("core");
  const { read } = props;
  if (read.state === "loading") {
    return (
      <p className="op-dialog-text op-workspace-status" role="status">
        {portalText("core", "account_admin.workspace_choice_loading")}
      </p>
    );
  }
  if (read.state === "failed") {
    return (
      <div className="op-workspace-status" role="alert">
        <p className="op-dialog-text">{read.problem}</p>
        <Button variant="secondary" onClick={read.retry}>
          {portalText("core", "account_admin.workspace_choice_try_again")}
        </Button>
      </div>
    );
  }
  if (read.workspace.length === 0) {
    return (
      <p className="op-dialog-text op-workspace-status" role="status">
        {portalText("core", "account_admin.workspace_choice_none")}
      </p>
    );
  }
  return null;
}
